import { transformTrackerEntries } from '../utils/transformTrackerEntries';
import type { HouseholdInput, StrategyName, ContributionInputType } from './index';
import type { ManualEntry, TaxCodeWarning } from './tracking';

/**
 * Backend entry format (before transformation).
 * Backend uses `type` field, frontend uses `kind` field.
 * Backend returns flat entries with payslip_id for grouping.
 */
export interface BackendEntry {
  id: string;
  type: 'income' | 'income_tax' | 'national_insurance' | 'contribution' | 'taxable_benefit';
  date: string;
  amount: string;
  payslip_id: string | null;
  payslip_tax_code?: string | null;
  payslip_tax_code_warnings?: TaxCodeWarning[] | null;
  contribution_type?: 'pension' | 'charity';
  payment_method?: 'relief_at_source' | 'salary_sacrifice';
  source?: 'user' | 'hmrc_topup' | 'gift_aid_topup';
}

/**
 * Household data stored in Django backend database.
 * Backend returns tracker entries in BackendEntry format (with `type` field).
 * Note: DRF serializes Decimal fields as strings, so numeric fields are string | number.
 */
export interface StoredHousehold {
  id: string;
  user_id: string;
  primary_gross_salary: number | string;
  primary_bonus?: number | string | null;
  primary_salary_sacrifice_contributions: number | string;
  primary_pension_contribution: number | string;
  primary_pension_contribution_unit: ContributionInputType;
  primary_pension_contribution_type: 'relief_at_source' | 'salary_sacrifice' | 'net_pay';
  primary_employer_pension_contribution: number | string;
  primary_employer_pension_contribution_unit: ContributionInputType;
  primary_payroll_giving_donations: number | string;
  primary_gift_aid_donations: number | string;
  primary_charity_contribution_type: 'gift_aid' | 'payroll_giving';
  primary_tax_code: string;
  partner_gross_salary?: number | string | null;
  partner_salary_sacrifice_contributions?: number | string | null;
  partner_net_pension_contributions?: number | string | null;
  partner_net_pension_contributions_type?: ContributionInputType | null;
  partner_employer_pension_contributions?: number | string | null;
  partner_employer_pension_contributions_type?: ContributionInputType | null;
  partner_payroll_giving_donations?: number | string | null;
  partner_gift_aid_donations?: number | string | null;
  partner_tax_code?: string | null;
  children?: Array<{ age: number }> | null;
  claims_child_benefit: boolean;
  preferred_strategy: StrategyName | null;
  payslip_reminder_day: number | null;
  // Backend returns entries with `type` field, not `kind`
  tracker_manual_entries: BackendEntry[] | null;
  created_at: string;
  updated_at: string;
}

/**
 * Input for creating or updating a household.
 * Excludes auto-generated fields (id, user_id, timestamps).
 */
export interface HouseholdUpsertInput {
  primary_gross_salary: number;
  primary_bonus?: number | null;
  primary_salary_sacrifice_contributions: number;
  primary_pension_contribution: number;
  primary_pension_contribution_unit: ContributionInputType;
  primary_pension_contribution_type: 'relief_at_source' | 'salary_sacrifice' | 'net_pay';
  primary_employer_pension_contribution: number;
  primary_employer_pension_contribution_unit: ContributionInputType;
  primary_payroll_giving_donations: number;
  primary_gift_aid_donations: number;
  primary_charity_contribution_type: 'gift_aid' | 'payroll_giving';
  primary_tax_code: string;
  partner_gross_salary?: number | null;
  partner_salary_sacrifice_contributions?: number | null;
  partner_net_pension_contributions?: number | null;
  partner_net_pension_contributions_type?: ContributionInputType | null;
  partner_employer_pension_contributions?: number | null;
  partner_employer_pension_contributions_type?: ContributionInputType | null;
  partner_payroll_giving_donations?: number | null;
  partner_gift_aid_donations?: number | null;
  partner_tax_code?: string | null;
  children: Array<{ age: number }>;
  claims_child_benefit: boolean;
  preferred_strategy: StrategyName | null;
  payslip_reminder_day?: number | null;
  tracker_manual_entries: ManualEntry[] | null;
}

/**
 * Converts HouseholdInput to HouseholdUpsertInput for database storage.
 */
export function householdInputToUpsert(input: HouseholdInput): HouseholdUpsertInput {
  // Select active contribution value based on unit type
  const primaryPensionContribution = input.primary.net_pension_contributions_type === 'percentage'
    ? (input.primary.net_pension_contributions_percentage ?? 0)
    : (input.primary.net_pension_contributions_amount ?? 0);

  const primaryEmployerPensionContribution = input.primary.employer_pension_contributions_type === 'percentage'
    ? (input.primary.employer_pension_contributions_percentage ?? 0)
    : (input.primary.employer_pension_contributions_amount ?? 0);

  const partnerNetPension = input.partner
    ? (input.partner.net_pension_contributions_type === 'percentage'
        ? (input.partner.net_pension_contributions_percentage ?? 0)
        : (input.partner.net_pension_contributions_amount ?? 0))
    : null;

  const partnerEmployerPension = input.partner
    ? (input.partner.employer_pension_contributions_type === 'percentage'
        ? (input.partner.employer_pension_contributions_percentage ?? 0)
        : (input.partner.employer_pension_contributions_amount ?? 0))
    : null;

  return {
    primary_gross_salary: input.primary.gross_salary,
    primary_bonus: input.primary.bonus ?? null,
    primary_salary_sacrifice_contributions: input.primary.salary_sacrifice_contributions,
    primary_pension_contribution: primaryPensionContribution,
    primary_pension_contribution_unit: input.primary.net_pension_contributions_type ?? 'amount',
    primary_pension_contribution_type: input.primary.pension_type ?? 'relief_at_source',
    primary_employer_pension_contribution: primaryEmployerPensionContribution,
    primary_employer_pension_contribution_unit: input.primary.employer_pension_contributions_type ?? 'amount',
    primary_payroll_giving_donations: input.primary.payroll_giving_donations,
    primary_gift_aid_donations: input.primary.gift_aid_donations,
    primary_charity_contribution_type: input.primary.charity_type ?? 'gift_aid',
    primary_tax_code: input.primary.tax_code,
    partner_gross_salary: input.partner?.gross_salary ?? null,
    partner_salary_sacrifice_contributions: input.partner?.salary_sacrifice_contributions ?? null,
    partner_net_pension_contributions: partnerNetPension,
    partner_net_pension_contributions_type: input.partner?.net_pension_contributions_type ?? null,
    partner_employer_pension_contributions: partnerEmployerPension,
    partner_employer_pension_contributions_type: input.partner?.employer_pension_contributions_type ?? null,
    partner_payroll_giving_donations: input.partner?.payroll_giving_donations ?? null,
    partner_gift_aid_donations: input.partner?.gift_aid_donations ?? null,
    partner_tax_code: input.partner?.tax_code ?? null,
    children: input.children ?? [],
    claims_child_benefit: input.claims_child_benefit ?? false,
    preferred_strategy: input.preferred_strategy ?? null,
    payslip_reminder_day: input.payslip_reminder_day ?? null,
    tracker_manual_entries: input.tracker_manual_entries ?? null,
  };
}

/**
 * Converts StoredHousehold from database to HouseholdInput for use in forms.
 * Transforms backend tracker entries (with `type` field) to frontend format (with `kind` field).
 */
export function storedHouseholdToInput(stored: StoredHousehold): HouseholdInput {
  const hasPartner = stored.partner_gross_salary !== null &&
                      stored.partner_gross_salary !== undefined;

  // Transform tracker entries from backend format to frontend format
  const transformedEntries = stored.tracker_manual_entries
    ? transformTrackerEntries(stored.tracker_manual_entries)
    : null;

  // Helper to ensure numeric values (backend may send strings)
  const toNumber = (val: number | string | null | undefined): number => {
    if (val === null || val === undefined) return 0;
    return typeof val === 'string' ? parseFloat(val) : val;
  };

  return {
    primary: {
      gross_salary: toNumber(stored.primary_gross_salary),
      bonus: stored.primary_bonus != null ? toNumber(stored.primary_bonus) : undefined,
      salary_sacrifice_contributions: toNumber(stored.primary_salary_sacrifice_contributions),
      net_pension_contributions_percentage: stored.primary_pension_contribution_unit === 'percentage'
        ? toNumber(stored.primary_pension_contribution)
        : 0,
      net_pension_contributions_amount: (stored.primary_pension_contribution_unit === 'amount' || stored.primary_pension_contribution_unit === undefined)
        ? toNumber(stored.primary_pension_contribution)
        : 0,
      net_pension_contributions_type: stored.primary_pension_contribution_unit,
      pension_type: stored.primary_pension_contribution_type,
      employer_pension_contributions_percentage: stored.primary_employer_pension_contribution_unit === 'percentage'
        ? toNumber(stored.primary_employer_pension_contribution)
        : 0,
      employer_pension_contributions_amount: (stored.primary_employer_pension_contribution_unit === 'amount' || stored.primary_employer_pension_contribution_unit === undefined)
        ? toNumber(stored.primary_employer_pension_contribution)
        : 0,
      employer_pension_contributions_type: stored.primary_employer_pension_contribution_unit,
      payroll_giving_donations: toNumber(stored.primary_payroll_giving_donations),
      gift_aid_donations: toNumber(stored.primary_gift_aid_donations),
      charity_type: stored.primary_charity_contribution_type ?? 'gift_aid',
      tax_code: stored.primary_tax_code,
    },
    partner: hasPartner ? {
      gross_salary: toNumber(stored.partner_gross_salary),
      salary_sacrifice_contributions: toNumber(stored.partner_salary_sacrifice_contributions),
      net_pension_contributions_percentage: stored.partner_net_pension_contributions_type === 'percentage'
        ? toNumber(stored.partner_net_pension_contributions)
        : 0,
      net_pension_contributions_amount: (stored.partner_net_pension_contributions_type === 'amount' || stored.partner_net_pension_contributions_type === undefined)
        ? toNumber(stored.partner_net_pension_contributions)
        : 0,
      net_pension_contributions_type: stored.partner_net_pension_contributions_type ?? 'amount',
      pension_type: 'salary_sacrifice', // Default for partners (not stored in backend)
      employer_pension_contributions_percentage: stored.partner_employer_pension_contributions_type === 'percentage'
        ? toNumber(stored.partner_employer_pension_contributions)
        : 0,
      employer_pension_contributions_amount: (stored.partner_employer_pension_contributions_type === 'amount' || stored.partner_employer_pension_contributions_type === undefined)
        ? toNumber(stored.partner_employer_pension_contributions)
        : 0,
      employer_pension_contributions_type: stored.partner_employer_pension_contributions_type ?? 'amount',
      payroll_giving_donations: toNumber(stored.partner_payroll_giving_donations),
      gift_aid_donations: toNumber(stored.partner_gift_aid_donations),
      charity_type: 'gift_aid', // Default
      tax_code: stored.partner_tax_code ?? '1257L',
    } : undefined,
    children: stored.children ?? [],
    claims_child_benefit: stored.claims_child_benefit,
    preferred_strategy: stored.preferred_strategy ?? null,
    payslip_reminder_day: stored.payslip_reminder_day ?? null,
    tracker_manual_entries: transformedEntries,
  };
}
